'use client'

import { useMemo } from 'react'
import type { BookmarkInfo } from '@/types/collections'
import { Badge } from '@/components/ui/badge'
import { IconBookmark } from '@tabler/icons-react'
import { cn } from '@/lib/utils'

function formatTimeAgo(date: string): string {
  const now = new Date()
  const then = new Date(date)
  const diffMs = now.getTime() - then.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)
  const diffWeeks = Math.floor(diffDays / 7)
  const diffMonths = Math.floor(diffDays / 30)
  const diffYears = Math.floor(diffDays / 365)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  if (diffWeeks < 4) return `${diffWeeks}w ago`
  if (diffMonths < 12) return `${diffMonths}mo ago`
  return `${diffYears}y ago`
}

interface BookmarkInfoBadgeProps {
  bookmarkInfo: BookmarkInfo
  className?: string
}

export function BookmarkInfoBadge({
  bookmarkInfo,
  className
}: BookmarkInfoBadgeProps) {
  const { bookmarked_at } = bookmarkInfo

  const timeAgoLabel = useMemo(() => {
    return formatTimeAgo(bookmarked_at)
  }, [bookmarked_at])

  const fullDate = useMemo(() => {
    return new Date(bookmarked_at).toLocaleString()
  }, [bookmarked_at])

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <Badge
        variant="outline"
        size="sm"
        className="gap-1"
        title={`Bookmarked: ${fullDate}`}
      >
        <IconBookmark className="h-3 w-3" />
        <span>Bookmarked {timeAgoLabel}</span>
      </Badge>
    </div>
  )
}
